/**
 * Router / GenerationProvider の外側でもマウントできる UI のための「安全版」フック。
 *
 * 素材プレビューモーダルは RouterProvider 配下からも、Router を持たない
 * 埋め込みコンテキストからも開かれる。react-router / GenerationProvider の
 * フックはコンテキスト外で呼ぶと throw するため、フォールバックを噛ませる。
 *
 * (旧: MaterialPreviewFooter.tsx と MaterialPreviewModal.tsx に同一実装が
 *  重複していたものを集約)
 */
import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { navigateInApp } from '../utils/navigation';
import { useGenerationAssetsSafe, type AssetsContextValue } from '../components/generation/state/assetsContext';
import { useGenerationJobsSafe, type JobsContextValue } from '../components/generation/state/jobsContext';
import { useGenerationSettingsSafe, type SettingsContextValue } from '../components/generation/state/settingsContext';

/**
 * Router 配下なら SPA 遷移、外側なら navigateInApp() へフォールバックする navigate。
 * options.state (遷移先に渡す履歴 state) は Router 配下でだけ効く。フォールバックでは落とす
 * (受け取る側は state が無くても動くこと)。
 */
export function useSafeNavigate(): (path: string, options?: { state?: unknown; replace?: boolean }) => void {
  try {
    return useNavigate();
  } catch {
    return (path) => navigateInApp(path);
  }
}

/** Router 配下なら useLocation()、外側なら window.location から pathname だけを返す。 */
export function useSafeLocation(): { pathname: string } {
  try {
    return useLocation();
  } catch {
    return { pathname: typeof window !== 'undefined' ? window.location.pathname : '/' };
  }
}

/**
 * 素材プレビューが必要とする生成 state のサブセット (素材 / 設定 / ジョブキュー)。
 * 属性ツリー (ScenarioAttributes) は使わないので購読しない。
 */
export type SafeGenerationState = AssetsContextValue & SettingsContextValue & JobsContextValue;

/**
 * GenerationProvider 配下なら生成 state (上記サブセット)、外側なら null。
 * Safe 版の個別フックを合成しているので、Provider 外でも throw しない
 * (旧実装は `useGenerationState()` を try/catch で包んでいた)。
 */
export function useSafeGenerationState(): SafeGenerationState | null {
  const assets = useGenerationAssetsSafe();
  const settings = useGenerationSettingsSafe();
  const jobs = useGenerationJobsSafe();
  return useMemo(() => {
    if (!assets || !settings || !jobs) return null;
    return { ...settings, ...assets, ...jobs };
  }, [assets, settings, jobs]);
}
